import * as Sentry from '@sentry/vue'
import loginApi from '@/modules/login/_api'
import usersApi from '@/modules/users/_api'
import router from '@/router'
import i18n, { setLanguage } from '@/locales'
import { storeTokenData } from '../_utils/utils'
import { segmentIdentify, segmentReset } from '@/analytics/segment'
import { segmentGroup } from '../../../analytics/segment'
import { COMPANIES_WITH_SPECIFIC_FEATURES } from '@/modules/transactions/_constants/constants'
import jwtDecode from 'jwt-decode'

export default {
  login: ({ commit, dispatch, getters }, authData) => {
    const loginCallback = loginApi.postLogin(authData)
    loginCallback
      .then(res => {
        if (res.data && res.data.token) {
          const user = jwtDecode(res.data.token)

          const userTraits = {
            user_id: user.user_id,
            username: user.firstName,
            email: user.email,
            signup_source: 'web',
            user_role: user.roles.join(',')
          }
          segmentIdentify(user.id, userTraits)

          const groupTraits = {
            id: user.customer_id,
            customer_name: user.customer_name,
            signup_source: 'web',
            customer_typology: 'empresa'
          }
          segmentGroup(user.customer_id, groupTraits)
          setLanguage(user.locale.substring(0, 2))
          storeTokenData({ commit, dispatch, getters }, res)
          let { id } = COMPANIES_WITH_SPECIFIC_FEATURES.find(el => el.company === 'PLEXUS')
          Sentry.setUser({ email: user.email })
          if ((user.isOwnerOfAnyBudget || user.isReviewer) && getters.isUser && user.office_id === id) {
            router.replace('/reimbursements/pending/users')
          } else {
            router.replace('/dashboard')
          }
        }
      })
      .catch(error => {
        if (error && error.response && error.response.status === 401) {
          if (error.response.data.message === 'Invalid credentials.') {
            dispatch(
              'SnackBars/addError',
              `${i18n.t(
                'login_action_login_snackbar_error_invalidad_credentials'
              )}`,
              { root: true }
            )
            return
          }
        }
        if (
          error &&
          error.response &&
          error.response.data &&
          error.response.data.detail
        ) {
          dispatch(
            'SnackBars/addError',
            `${i18n.t('login_action_login_snackbar_error')}: ${error.response.data.detail
            }`,
            { root: true }
          )
          return
        }
        if (error && error.response && error.response.data.message) {
          dispatch(
            'SnackBars/addError',
            `${i18n.t('login_action_login_snackbar_error')}: ${error.response.data.message
            }`,
            { root: true }
          )
          return
        }
        dispatch(
          'SnackBars/addError',
          i18n.t('login_action_login_snackbar_error'),
          { root: true }
        )
      })
  },
  acceptInvitation: async (
    { getters, dispatch, commit },
    { invitationData, selfOnboarding = false }
  ) => {
    if (getters.isCallingLogin) return
    try {
      commit('setIsCallingLogin', true)

      let response
      if (selfOnboarding) {
        response = await loginApi.postActivateUserPublic(invitationData)
      } else {
        response = await loginApi.postActivateUserInvitations(
          invitationData
        )
      }

      const user = jwtDecode(response.data.token)
      const userTraits = {
        user_id: user.id,
        username: user.firstName,
        email: user.email,
        signup_source: 'web',
        user_role: user.roles.join(','),
        onboarding_status: 'ONBOARDING_COMPLETED'
      }

      const groupTraits = {
        id: user.customer_id,
        customer_name: user.customer_name,
        signup_source: 'web',
        customer_typology: 'empresa'
      }
      segmentIdentify(user.id, userTraits)

      segmentGroup(user.customer_id, groupTraits)

      storeTokenData({ commit, dispatch, getters }, response)
      if (!selfOnboarding) {
        dispatch(
          'SnackBars/addSuccessOnboarding',
          i18n.t('login_action_accept_invitation_success'),
          { root: true }
        )
        router.push({ path: '/dashboard' })
      }
    } catch (error) {
      if (!selfOnboarding) {
        dispatch(
          'SnackBars/addErrorHandler',
          { error: error, errorMessage: 'login_action_accept_invitation_error' },
          { root: true }
        )
      }
      throw error
    } finally {
      commit('setIsCallingLogin', false)
    }
  },
  requestResetPassword: async ({ commit, getters, dispatch }, recoveryInfo) => {
    if (getters.isCallingLogin) return
    return new Promise(async (resolve, reject) => {
      commit('setIsCallingLogin', true)

      try {
        await loginApi.postRequestResetPassword(recoveryInfo)
        // dispatch('SnackBars/addInfo', i18n.t('login_action_request_reset_password_info'), { root: true })
        return resolve()
      } catch (error) {
        // dispatch('SnackBars/addErrorHandler', { error: error, errorMessage: 'login_action_request_reset_password_error' }, { root: true })
        return reject(error)
      } finally {
        commit('setIsCallingLogin', false)
      }
    })
  },
  resetPassword: async ({ commit, getters, dispatch }, recoveryInformation) => {
    if (getters.isCallingLogin) return
    return new Promise(async (resolve, reject) => {
      try {
        commit('setIsCallingLogin', true)
        if (recoveryInformation && !recoveryInformation.token) return
        const response = await loginApi.postResetPassword({
          password: recoveryInformation.password,
          token: recoveryInformation.token
        })
        dispatch(
          'SnackBars/addSuccess',
          i18n.t('login_action_reset_password_success'),
          { root: true }
        )
        resolve(response)
      } catch (error) {
        dispatch(
          'SnackBars/addErrorHandler',
          { error: error, errorMessage: 'login_action_reset_password_error' },
          { root: true }
        )
        reject(error)
      } finally {
        commit('setIsCallingLogin', false)
      }
    })
  },
  setTokenTimer: ({ commit, dispatch, state }, expirationTime) => {
    const currentTimer = state.logoutTimer ? state.logoutTimer : 0
    if (currentTimer) {
      return
    }
    const secondsToExpiration = Number.parseInt(expirationTime)
    let timer = setTimeout(() => {
      dispatch('refreshToken')
    }, secondsToExpiration)
    commit('refreshTokenTimer', timer)
  },

  refreshToken: async ({ commit, dispatch, state, getters }) => {
    const refreshToken = localStorage.getItem('refreshToken')
    try {
      const refresh = await loginApi.postRefreshToken(refreshToken)
      storeTokenData({ commit, dispatch, getters }, refresh)
    } catch (error) {
      dispatch('logout')
    }
  },

  tryAutoLogin: async ({ commit, dispatch, getters }) => {
    const token = localStorage.getItem('token')
    //    let useCallbacks = typeof args !== 'undefined'
    /*
    if (!token && !args.to.meta.isPublic) {
      dispatch('logout', { redirect: false })

      if (useCallbacks) args.next('/signin')
      return
    } */
    const expirationDate = localStorage.getItem('expirationDate')
      ? new Date(localStorage.getItem('expirationDate'))
      : 0
    const now = new Date()
    //    if (now >= expirationDate && !args.to.meta.isPublic) {
    if (now >= expirationDate) {
      await dispatch('refreshToken')

      // if (useCallbacks) args.next()
      return
    }
    const user = token ? jwtDecode(token) : null
    if (!getters.isAuthenticated && user) {
      commit('authUser', {
        token: token,
        user: user
      })
      const secondsToLogout = expirationDate > 0 ? expirationDate - now : 0
      dispatch('setTokenTimer', secondsToLogout)
    }

    // if (useCallbacks) args.next()
  },

  logout: (
    { commit, dispatch, getters },
    { notifyUser = false, redirect = true } = {}
  ) => {
    if (getters.refreshTokenTimer) clearTimeout(getters.refreshTokenTimer)
    commit('clearAuthData')
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('expirationDate')
    dispatch('clearStores')
    segmentReset()
    Sentry.setUser(null)
    if (redirect) router.replace('/signin')
  },
  impersonateUser: async ({ dispatch }, userId) => {
    try {
      const userToImpersonate = await usersApi.getUser(userId)
      const { office } = userToImpersonate.data
      if (office && office.id) {
        await dispatch('CompanyStore/fetchCompany', office.id, { root: true })
      }
      dispatch('GlobalStore/setInitializedData', false, { root: true })
      dispatch('_impersonate', userToImpersonate.data)
      dispatch(
        'SnackBars/addSuccess',
        i18n.t('login_action_impersonate_user_info', {
          user: `${userToImpersonate.data.firstName} ${userToImpersonate.data.lastName}`
        }),
        { root: true }
      )
    } catch (error) { }
  },
  stopImpersonation: ({ commit, dispatch }) => {
    commit('setImpersonatedUser', null)
    dispatch('CompanyStore/clearCompany', null, { root: true })
    dispatch(
      'SnackBars/addSuccess',
      i18n.t('login_action_stop_impersonation_info'),
      { root: true }
    )
    dispatch('GlobalStore/initializeData', null, { root: true })
    // eslint-disable-next-line
    router.push({ path: '/dashboard' }).catch(error => { })
  },
  _impersonate: ({ commit }, user) => {
    commit('setImpersonatedUser', user)
    try {
      // eslint-disable-next-line
      router.push({ query: { supplanting: true } }).catch(error => { })
    } catch (error) { }
  },
  clearAuthData: ({ commit, dispatch, getters }) => {
    if (getters.refreshTokenTimer) clearTimeout(getters.refreshTokenTimer)
    commit('clearAuthData')
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('expirationDate')
    dispatch('clearStores')
  },
  clearStore: ({ commit }) => {
    commit('clearStoreData')
  },
  clearStores: ({ dispatch }) => {
    const storesToClear = [
      'LoginStore',
      'UsersStore',
      'CategoriesStore',
      'BudgetsStore',
      'CardsStore',
      'CustomersStore',
      'TransactionsStore',
      'ResourcesStore',
      'LogsStore',
      'DashboardStore',
      'CompanyStore',
      'CompanyStore/TransactionFieldsStore',
      'CompanyStore/ExpensesSettingsStore',
      'CompanyStore/ExportSettingsStore',
      'CompanyStore/AlertsSettingsStore',
      'BenefitsStore',
      'GlobalStore'
    ]
    storesToClear.forEach(store =>
      dispatch(`${store}/clearStore`, null, { root: true })

    )
  }
}
